import { type Monitor, batch } from "@suse-o11y/signal";
import { toJSON } from "@suse-o11y/to-json";
import sumBy from "lodash/sumBy";

import { PinnedItemsSectionState } from "./_pinned-items-section";
import type { PinnedMetric } from "./metric";
import type { PinnedItemData, PinnedMonitorStatus } from "./PinnedItem";
import { PinnedItem } from "./PinnedItem";

export interface InitialPinnedItemsState {
	monitorStatuses?: ReadonlyArray<PinnedMonitorStatus>;
	metrics?: ReadonlyArray<PinnedMetric>;
}

export class PinnedItemsState {
	public static init({ monitorStatuses, metrics }: InitialPinnedItemsState = {}): PinnedItemsState {
		return new PinnedItemsState([
			PinnedItemsSectionState.init({
				items: monitorStatuses,
				getItemIdentifier: ({ id }) => id.toString(),
			}),

			PinnedItemsSectionState.init({
				shouldCollapseAllOnPin: false,
				getItemIdentifier: (metric) => toJSON(metric, 0),
				items: metrics,
			}),
		]);
	}

	private constructor(
		/**
		 * By storing the sections in a tuple we have an easy way to iterate over them.
		 */
		private readonly sections: [PinnedItemsSectionState<PinnedMonitorStatus>, PinnedItemsSectionState<PinnedMetric>],
	) {}

	public get monitorStatuses(): PinnedItemsSectionState<PinnedMonitorStatus> {
		return this.sections[0];
	}

	public get metrics(): PinnedItemsSectionState<PinnedMetric> {
		return this.sections[1];
	}

	/**
	 * The method uses duck typing to match the PinnedItemData with the relative section.
	 */
	public pin(data: PinnedItemData): void {
		batch(() => {
			// collapse auto collapsing sections
			this.sections.forEach((section) => {
				section.isExpanded.write(false);
			});

			PinnedItem.cata(data, {
				// biome-ignore lint/nursery/noComponentHookFactories: false-positive due to PascalCase
				MonitorStatus: (monitorStatus) =>
					this.monitorStatuses.pin({
						...monitorStatus,
						timeRange: monitorStatus.timeRange.clone(),
					}),
				// biome-ignore lint/nursery/noComponentHookFactories: false-positive due to PascalCase
				Metric: (metric) => this.metrics.pin(metric),
			});
		});
	}

	public unpin(data: PinnedItemData): void {
		PinnedItem.cata(data, {
			MonitorStatus: (monitorStatus) => this.monitorStatuses.unpin(monitorStatus),
			Metric: (metric) => this.metrics.unpin(metric),
		});
	}

	public isPinned(monitor: Monitor, data: PinnedItemData): boolean {
		return PinnedItem.cata(data, {
			MonitorStatus: (monitorStatus) => this.monitorStatuses.isPinned(monitor, monitorStatus),
			Metric: (metric) => this.metrics.isPinned(monitor, metric),
		});
	}

	public size(monitor: Monitor): number {
		return sumBy(this.sections, (section) => section.getItems(monitor).length);
	}
}
